import type { AxiosInstance } from 'axios';
import authService from './auth.service';

/**
 * Client-initiated "raise hand for a callback" API.
 *
 * Used by the engagement dashboard / yield simulator to let a client
 * ask their assigned advisor for a call about a specific product,
 * without committing to a scheduled time. The agent receives an email
 * + a queue entry; they call back at their next available slot.
 *
 * Endpoint: POST /api/v1/clients/{client_id}/callback-requests
 */

export interface CallbackRequestResult {
  request_id: string;
  status: string;
  /** True when the backend's idempotent path returned an existing
   *  pending request rather than creating a new one. Lets the UI
   *  distinguish "we just told your advisor" from "we already told
   *  your advisor, sit tight". */
  already_existed: boolean;
  advisor_first_name: string | null;
  advisor_last_name: string | null;
  /** False when the client has no assigned agent yet — the request was
   *  recorded as an orphan and will reach an advisor once a manager assigns
   *  one. Drives the "an advisor will reach out" copy (no advisor name). */
  assigned: boolean;
}

export interface CallbackRequestBody {
  product_id?: string;
  context_note?: string;
}

class CallbacksService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async requestCallback(body: CallbackRequestBody = {}): Promise<CallbackRequestResult> {
    const clientId = await authService.getMyId();
    const res = await this.api.post<CallbackRequestResult>(
      `/api/v1/clients/${clientId}/callback-requests`,
      body,
    );
    return res.data;
  }
}

export default new CallbacksService();
